// src/components/MatchAnalyticsModal.jsx
import React, { useState } from 'react';
import Modal from './Modal';
import FormIndicator from './FormIndicator';
import WeatherBadge from './WeatherBadge';
import useSubscriptionTier from '../hooks/useSubscriptionTier';
import { getAnalyticsDepth } from '../lib/subscriptionGating';
import { supabase } from '../lib/supabaseClient';
import { Button as FcButton } from '../ui';

/**
 * MatchAnalyticsModal - Displays detailed match analytics with tier-based gating
 * Tiers unlock LEAGUES, analytics depth varies by tier
 * @param {boolean} isOpen - Modal visibility state
 * @param {function} onClose - Close modal callback
 * @param {object} fixture - Fixture object with match details
 * @param {object} analytics - Analytics data object containing form, h2h, venue, weather
 */
export default function MatchAnalyticsModal({ isOpen, onClose, fixture, analytics }) {
  const [session, setSession] = useState(null);
  
  // Get user's subscription tier
  React.useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
    });
  }, []);
  
  const { tier } = useSubscriptionTier(session?.user?.id);
  
  if (!fixture) return null;
  
  const analyticsDepth = getAnalyticsDepth(tier);

  const { home_team, away_team, kickoff_time, venue } = fixture;
  const { homeForm, awayForm, h2h, venueStats, awayTeamStats, weather } = analytics || {};

  const kickoffDate = kickoff_time ? new Date(kickoff_time).toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  }) : '';
  
  // Determine what to show based on analytics depth
  const showBasicOnly = analyticsDepth === 'basic'; // Starter: simple form only
  const showStandard = ['standard', 'advanced', 'premium'].includes(analyticsDepth); // Observer/Strategist+
  const showAdvanced = ['advanced', 'premium'].includes(analyticsDepth); // Plus modes
  const showPremium = analyticsDepth === 'premium'; // Analyst+

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} title="Match Analytics">
        <div className="space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="text-center border-b pb-3 sm:pb-4 pr-6">
          <h2 className="text-lg sm:text-2xl font-bold text-content-primary">
            {home_team} <span className="text-content-secondary">vs</span> {away_team}
          </h2>
          <p className="text-xs sm:text-sm text-content-secondary mt-1">{kickoffDate}</p>
          {venue && <p className="text-xs sm:text-sm text-content-secondary">{venue}</p>}
        </div>

        {/* Form Analysis */}
        <section>
          <h3 className="text-base sm:text-lg font-semibold text-content-primary mb-2 sm:mb-3 flex items-center gap-2">
            <span>📈</span> Form Analysis
          </h3>
          
          {showBasicOnly ? (
            // Starter: Basic form - just W/L/D icons, no summaries
            <div className="space-y-3">
              <div>
                <div className="flex justify-between items-center">
                  <span className="font-medium text-content-primary">{home_team}</span>
                  {homeForm?.results && <FormIndicator results={homeForm.results} />}
                </div>
              </div>
              <div>
                <div className="flex justify-between items-center">
                  <span className="font-medium text-content-primary">{away_team}</span>
                  {awayForm?.results && <FormIndicator results={awayForm.results} />}
                </div>
              </div>
              <div className="mt-3 p-3 bg-surface-canvas rounded-lg border border-blue-200">
                <p className="text-xs text-content-primary">
                  💡 Upgrade to <strong>Observer</strong> for detailed form analysis with summaries and insights
                </p>
              </div>
            </div>
          ) : (
            // Standard: Detailed form with summaries
            <div className="space-y-3">
              <div>
                <div className="flex justify-between items-center">
                  <span className="font-medium text-content-primary">{home_team}</span>
                  {homeForm?.results && <FormIndicator results={homeForm.results} />}
                </div>
                {homeForm?.summary && (
                  <p className="text-sm text-content-secondary mt-1">{homeForm.summary}</p>
                )}
              </div>
              <div>
                <div className="flex justify-between items-center">
                  <span className="font-medium text-content-primary">{away_team}</span>
                  {awayForm?.results && <FormIndicator results={awayForm.results} />}
                </div>
                {awayForm?.summary && (
                  <p className="text-sm text-content-secondary mt-1">{awayForm.summary}</p>
                )}
              </div>
            </div>
          )}
        </section>

        {/* Head-to-Head - Standard tier (Observer/Strategist) */}
        {showStandard && (
          <section>
            <h3 className="text-base sm:text-lg font-semibold text-content-primary mb-2 sm:mb-3 flex items-center gap-2">
              <span>🤝</span> Head-to-Head
            </h3>
            {h2h ? (
              <div className="space-y-1 sm:space-y-2 text-xs sm:text-sm text-content-primary">
                {h2h.summary && <p>{h2h.summary}</p>}
                {h2h.lastMeetings && <p>{h2h.lastMeetings}</p>}
                {h2h.avgGoals && <p>Average goals per game: {h2h.avgGoals}</p>}
              </div>
            ) : (
              <p className="text-xs sm:text-sm text-content-secondary italic">No head-to-head data available</p>
            )}
          </section>
        )}

        {/* Home Team Stats - Standard tier (Observer/Strategist) */}
        {showStandard && (
          <section>
            <h3 className="text-base sm:text-lg font-semibold text-content-primary mb-2 sm:mb-3 flex items-center gap-2">
              <span>🏟️</span> {home_team} Home Stats
            </h3>
            {venueStats ? (
            <div className="space-y-3 sm:space-y-4">
              {/* Last 5 Home Games */}
              {venueStats.last5Home && venueStats.last5Home.totalGames > 0 && (
                <div className="border-l-2 border-green-500 pl-3">
                  <div className="flex justify-between items-center mb-1">
                    <p className="font-semibold text-content-primary text-xs sm:text-sm">Last 5 Home Games</p>
                    {venueStats.last5Home.results && venueStats.last5Home.results.length > 0 && (
                      <FormIndicator results={venueStats.last5Home.results} />
                    )}
                  </div>
                  <p className="text-xs sm:text-sm text-content-primary">
                    Record: <span className="font-medium">{venueStats.last5Home.record}</span>
                  </p>
                  <p className="text-xs sm:text-sm text-content-secondary">
                    Goals: {venueStats.last5Home.goalsScored} scored, {venueStats.last5Home.goalsConceded} conceded
                  </p>
                </div>
              )}
              
              {/* Current Season */}
              {venueStats.currentSeason && venueStats.currentSeason.totalGames > 0 && (
                <div className="border-l-2 border-blue-500 pl-3">
                  <p className="font-semibold text-content-primary text-xs sm:text-sm mb-1">Current Season</p>
                  <p className="text-xs sm:text-sm text-content-primary">
                    Record: <span className="font-medium">{venueStats.currentSeason.record}</span>
                  </p>
                  <p className="text-xs sm:text-sm text-content-secondary">
                    Goals: {venueStats.currentSeason.goalsScored} scored, {venueStats.currentSeason.goalsConceded} conceded
                  </p>
                  <p className="text-xs text-content-secondary">({venueStats.currentSeason.totalGames} home {venueStats.currentSeason.totalGames === 1 ? 'game' : 'games'})</p>
                </div>
              )}
              
              {/* Lifetime Results */}
              {venueStats.lifetime && venueStats.lifetime.totalGames > 0 && (
                <div className="border-l-2 border-purple-500 pl-3">
                  <p className="font-semibold text-content-primary text-xs sm:text-sm mb-1">Lifetime at Home</p>
                  <p className="text-xs sm:text-sm text-content-primary">
                    Record: <span className="font-medium">{venueStats.lifetime.record}</span>
                  </p>
                  <p className="text-xs sm:text-sm text-content-secondary">
                    Goals: {venueStats.lifetime.goalsScored} scored, {venueStats.lifetime.goalsConceded} conceded
                  </p>
                  <p className="text-xs text-content-secondary">({venueStats.lifetime.totalGames} total home {venueStats.lifetime.totalGames === 1 ? 'game' : 'games'})</p>
                </div>
              )}
            </div>
            ) : (
              <p className="text-xs sm:text-sm text-content-secondary italic">No venue stats available</p>
            )}
          </section>
        )}

        {/* Away Team Stats - Standard tier (Observer/Strategist) */}
        {showStandard && (
          <section>
            <h3 className="text-base sm:text-lg font-semibold text-content-primary mb-2 sm:mb-3 flex items-center gap-2">
              <span>✈️</span> {away_team} Away Stats
            </h3>
            {awayTeamStats ? (
            <div className="space-y-3 sm:space-y-4">
              {/* Last 5 Away Games */}
              {awayTeamStats.last5Away && awayTeamStats.last5Away.totalGames > 0 && (
                <div className="border-l-2 border-green-500 pl-3">
                  <div className="flex justify-between items-center mb-1">
                    <p className="font-semibold text-content-primary text-xs sm:text-sm">Last 5 Away Games</p>
                    {awayTeamStats.last5Away.results && awayTeamStats.last5Away.results.length > 0 && (
                      <FormIndicator results={awayTeamStats.last5Away.results} />
                    )}
                  </div>
                  <p className="text-xs sm:text-sm text-content-primary">
                    Record: <span className="font-medium">{awayTeamStats.last5Away.record}</span>
                  </p>
                  <p className="text-xs sm:text-sm text-content-secondary">
                    Goals: {awayTeamStats.last5Away.goalsScored} scored, {awayTeamStats.last5Away.goalsConceded} conceded
                  </p>
                </div>
              )}
              
              {/* Current Season Away */}
              {awayTeamStats.currentSeason && awayTeamStats.currentSeason.totalGames > 0 && (
                <div className="border-l-2 border-blue-500 pl-3">
                  <p className="font-semibold text-content-primary text-xs sm:text-sm mb-1">Current Season Away</p>
                  <p className="text-xs sm:text-sm text-content-primary">
                    Record: <span className="font-medium">{awayTeamStats.currentSeason.record}</span>
                  </p>
                  <p className="text-xs sm:text-sm text-content-secondary">
                    Goals: {awayTeamStats.currentSeason.goalsScored} scored, {awayTeamStats.currentSeason.goalsConceded} conceded
                  </p>
                  <p className="text-xs text-content-secondary">({awayTeamStats.currentSeason.totalGames} away {awayTeamStats.currentSeason.totalGames === 1 ? 'game' : 'games'})</p>
                </div>
              )}
              
              {/* Lifetime Away Results */}
              {awayTeamStats.lifetime && awayTeamStats.lifetime.totalGames > 0 && (
                <div className="border-l-2 border-purple-500 pl-3">
                  <p className="font-semibold text-content-primary text-xs sm:text-sm mb-1">Lifetime Away</p>
                  <p className="text-xs sm:text-sm text-content-primary">
                    Record: <span className="font-medium">{awayTeamStats.lifetime.record}</span>
                  </p>
                  <p className="text-xs sm:text-sm text-content-secondary">
                    Goals: {awayTeamStats.lifetime.goalsScored} scored, {awayTeamStats.lifetime.goalsConceded} conceded
                  </p>
                  <p className="text-xs text-content-secondary">({awayTeamStats.lifetime.totalGames} total away {awayTeamStats.lifetime.totalGames === 1 ? 'game' : 'games'})</p>
                </div>
              )}
            </div>
            ) : (
              <p className="text-xs sm:text-sm text-content-secondary italic">No away stats available</p>
            )}
          </section>
        )}

        {/* Weather - Standard tier (Observer/Strategist) */}
        {showStandard && (
          <section>
            <h3 className="text-base sm:text-lg font-semibold text-content-primary mb-2 sm:mb-3 flex items-center gap-2">
              <span>🌤️</span> Weather
            </h3>
            {weather && (weather.condition || weather.temperature !== undefined) ? (
            <div className="space-y-1 sm:space-y-2">
              <div className="flex items-center gap-2">
                <WeatherBadge condition={weather.condition} temperature={weather.temperature} size="md" />
                {weather.description && (
                  <span className="text-content-secondary text-xs sm:text-sm">{weather.description}</span>
                )}
              </div>
              {weather.impact && (
                <p className="text-xs sm:text-sm text-content-secondary italic">{weather.impact}</p>
              )}
              {weather.wind && <p className="text-xs sm:text-sm text-content-secondary">Wind: {weather.wind}</p>}
            </div>
            ) : (
              <p className="text-xs sm:text-sm text-content-secondary italic">Weather data not available</p>
            )}
          </section>
        )}

        {/* Close Button */}
        <div className="flex justify-end mt-4">
          <FcButton variant="tertiary" type="submit"
            onClick={onClose}
            className=""
          >
            Close
          </FcButton>
        </div>
      </div>
      </Modal>
    </>
  );
}
